import { AnimatePresence, motion, MotionProps, useReducedMotion } from 'framer-motion';
import { Send } from 'lucide-react';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscordAddToServerCta } from './DiscordAddToServerCta';
import { SelectablePill } from './SelectablePill';
import { useOnboardingBrand, useOnboardingPortalTheme } from './onboardingBrand';
import { CHANNEL_DISCORD, CHANNEL_OTHER_ID, CHANNEL_TELEGRAM, CHANNELS } from './onboardingOptions';
import { useGettingStartedOnboarding } from './useGettingStartedOnboarding';
import { TelegramSheet } from '@/Domain/components/Integration/Telegram/Telegram.sheet';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

export const DeploymentStep = () => {
  const { t } = useTranslation('getting_started');
  const { capture } = usePostHogAnalytics();
  const {
    deploymentSelection,
    toggleDeploymentChannel,
    deploymentOtherText,
    setDeploymentOtherText,
  } = useGettingStartedOnboarding();
  const { brand } = useOnboardingBrand();
  const portalTheme = useOnboardingPortalTheme();
  const reduceMotion = useReducedMotion();
  const [telegramSheetOpen, setTelegramSheetOpen] = useState(false);
  const otherSelected = deploymentSelection.has(CHANNEL_OTHER_ID);
  // Discord-branded when the user was attributed to Discord (cohort B) or picks it here (cohort A).
  // The whole-flow deep-space background + lockup are driven by the page shell off the same
  // effective brand; this local check also covers the isolated (no-provider) render in tests.
  const discordBranded = brand === 'discord' || deploymentSelection.has(CHANNEL_DISCORD);
  const telegramSelected = deploymentSelection.has(CHANNEL_TELEGRAM);
  const panelMotion: MotionProps = {
    initial: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 },
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  };

  const openTelegramSheet = () => {
    capture(PostHogEventEnum.GettingStartedTelegramCtaClicked);
    setTelegramSheetOpen(true);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" data-testid="gs-deployment-pills">
        {CHANNELS.map((channel) => (
          <SelectablePill
            key={channel.id}
            label={channel.label}
            icon={channel.icon}
            iconColor={channel.iconColor}
            selected={deploymentSelection.has(channel.id)}
            onToggle={() => toggleDeploymentChannel(channel.id)}
            testId={`gs-deployment-pill-${channel.id}`}
          />
        ))}
      </div>
      {otherSelected && (
        <Input
          type="text"
          value={deploymentOtherText}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDeploymentOtherText(e.target.value)}
          placeholder={t('deployment_step.other_placeholder')}
          maxLength={200}
          autoComplete="off"
          data-testid="gs-deployment-other-input"
        />
      )}
      <AnimatePresence initial={false}>
        {discordBranded && (
          <motion.div
            key="discord-panel"
            data-brand="discord"
            data-testid="gs-deployment-discord-panel"
            {...panelMotion}
            className="mt-2 flex flex-col gap-4 rounded-xl border border-[var(--qc-discord-border)] bg-[var(--qc-discord-surface)] p-5"
          >
            <p className="text-sm font-medium text-[var(--qc-discord-on-surface)]">
              {t('automatic_flow.discord.deployment_prompt')}
            </p>
            <DiscordAddToServerCta source="deployment_step" />
          </motion.div>
        )}
        {telegramSelected && (
          <motion.div
            key="telegram-panel"
            data-testid="gs-deployment-telegram-panel"
            {...panelMotion}
            className="mt-2 flex flex-col gap-4 rounded-xl border border-border bg-muted/40 p-5"
          >
            <p className="text-sm font-medium text-foreground">
              {t('deployment_step.telegram_prompt')}
            </p>
            <Button
              type="button"
              onClick={openTelegramSheet}
              data-testid="gs-deployment-telegram-connect"
              className="w-full"
            >
              <Send className="size-4" aria-hidden />
              {t('deployment_step.telegram_button')}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      {telegramSelected && (
        <TelegramSheet
          open={telegramSheetOpen}
          onOpenChange={setTelegramSheetOpen}
          contentClassName={portalTheme}
        />
      )}
    </div>
  );
};
